/**
 * The website agent's nudges arrive in two calls.
 *
 * `validate-client-key` is asked with the page the visitor is on and carries
 * only that page's nudges, so the first pill costs the config call nothing
 * for the other pages. When floe-api says there are more (`hasMore`), the
 * SDK loads the site's whole list from `POST /sites/web-agent/nudges` once the
 * host page has loaded and the browser is idle, or as soon as the visitor
 * moves to another page WITHOUT a reload, and swaps it in. That is what gives
 * such a visitor the new page's pills.
 *
 * The whole list replaces the page's rows; it is not merged into them. The
 * two calls give a row the same id, so a pill on screen stays put, and a row
 * the server's page match left out is only late, never lost.
 *
 * A failed second call keeps what the first one brought. It is tried again
 * when the visitor next moves to another page without a reload, which is the
 * one moment the whole list is needed, and not after a third failure.
 */
import { isWebsiteAgent } from './agent-mode';
import type { AgentModeFlags } from './agent-mode';

export interface WebsiteAgentNudgeRow {
  copy: string;
  enabled?: boolean;
  id: string;
  match: string;
  page?: string;
  topic?: string;
}

/** What the two responses may hold where a row is expected. */
type RowCandidate = Partial<WebsiteAgentNudgeRow> | null | undefined;

// A server-capped payload, validated all the same: one malformed row must not
// take the launcher down with it. The field is JSON, so it may be any JSON
// value, and only a string is equal to its own String().
const hasText = (value: string | undefined): boolean =>
  String(value) === value && String(value).trim().length > 0;

const isUsable = (row: RowCandidate): row is WebsiteAgentNudgeRow =>
  Boolean(row) && hasText(row?.id) && hasText(row?.copy);

/** The rows with a usable id and copy, or undefined when there is no list. */
export const usableNudges = (rows?: RowCandidate[]): WebsiteAgentNudgeRow[] | undefined =>
  Array.isArray(rows) ? rows.filter((row) => isUsable(row)) : undefined;

/**
 * The page path the config call names, for the website agent only. The demo
 * agent shows no nudges, and the onboarding agent runs inside a signed-in
 * product, whose paths are the customer's own data and are not ours to send.
 */
export const nudgeRequestPath = (
  config: AgentModeFlags,
  pathname?: string,
): string | undefined =>
  isWebsiteAgent(config) && pathname ? pathname : undefined;

const LOAD_TIMEOUT_MS = 8000;

/** Failed loads of a site's whole list after which the SDK stops asking. */
export const MAX_NUDGE_LOAD_FAILURES = 3;

/** The site's whole list, or why it could not be loaded (for the debug log). */
export type NudgeLoad =
  | { ok: true; rows: WebsiteAgentNudgeRow[] }
  | { ok: false; reason: string };

export const fetchAllNudges = async (apiUrl: string, clientKey: string): Promise<NudgeLoad> => {
  // An AbortController and a timer, not AbortSignal.timeout: Safari 15 has no
  // such method, and there this load would fail on every page.
  const timeout = new AbortController();
  const timer = setTimeout(() => timeout.abort(), LOAD_TIMEOUT_MS);
  try {
    const response = await fetch(`${apiUrl}/sites/web-agent/nudges`, {
      body: JSON.stringify({ clientKey }),
      // Anonymous (clientKey + Origin), like validate-client-key.
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      signal: timeout.signal,
    });
    if (!response.ok) {
      return { ok: false, reason: `HTTP ${response.status}` };
    }
    const body: { contentMap?: RowCandidate[] } = await response.json();
    const rows = usableNudges(body.contentMap);
    return rows ? { ok: true, rows } : { ok: false, reason: 'no list in the response' };
  } catch {
    return { ok: false, reason: timeout.signal.aborted ? 'timed out' : 'network error' };
  } finally {
    clearTimeout(timer);
  }
};

const IDLE_TIMEOUT_MS = 3000;
const FALLBACK_DELAY_MS = 1200;

/**
 * Run `task` once the host page has loaded and the browser is idle: the rest
 * of the nudges are for a page the visitor has not gone to yet, so they wait
 * behind everything the page itself needs. Returns a cancel function.
 */
export const afterLoadWhenIdle = (task: () => void): (() => void) => {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let idleHandle: number | undefined;
  const run = () => {
    if (!cancelled) {
      task();
    }
  };
  const whenIdle = () => {
    if (cancelled) {
      return;
    }
    // Safari has no requestIdleCallback: it takes the timer below.
    if ('requestIdleCallback' in window) {
      idleHandle = window.requestIdleCallback(run, { timeout: IDLE_TIMEOUT_MS });
      return;
    }
    timer = setTimeout(run, FALLBACK_DELAY_MS);
  };
  if (document.readyState === 'complete') {
    whenIdle();
  } else {
    window.addEventListener('load', whenIdle, { once: true });
  }
  return () => {
    cancelled = true;
    window.removeEventListener('load', whenIdle);
    if (timer !== undefined) {
      clearTimeout(timer);
    }
    if (idleHandle !== undefined) {
      window.cancelIdleCallback?.(idleHandle);
    }
  };
};
